import { Badge, Button } from '@ui'
import { LinkPrevious } from 'grommet-icons'
import type {
  ChangeEvent,
  ComponentProps,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes
} from 'react'
import type { DispenseStatus, MedStatus } from '../types'
import { BUY_OUTSIDE_LABEL } from '../utils/supply'

export const formatMoney = (n: number) => `₦${n.toLocaleString()}`

export const patientLabel = (p?: { attributes: { first_name: string; last_name: string; ref: string } }) =>
  p ? `${p.attributes.first_name} ${p.attributes.last_name} (${p.attributes.ref})` : '—'

type MedDoseAttrs = {
  dose_quantity?: string
  dose_unit?: string
  strength?: string
  form?: string
  start_at?: string
  end_at?: string
  frequency_label?: string
}

/** Same dosage pattern as Orders table: `1tablet · 500mg · Tablet` */
export const formatMedDosage = (a: MedDoseAttrs) => {
  const dose = `${a.dose_quantity || ''}${a.dose_unit || ''}`.trim()
  return [dose || null, a.strength || null, a.form || null].filter(Boolean).join(' · ') || '—'
}

const formatMedDate = (iso?: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Order start–end period, or — if neither is set */
export const formatMedDateRange = (a: MedDoseAttrs) => {
  const start = formatMedDate(a.start_at)
  const end = formatMedDate(a.end_at)
  if (!start && !end) return '—'
  if (start && end) return `${start} – ${end}`
  return start || end
}

/** Compact queue secondary line: dosage · frequency */
export const formatMedQueueHint = (a: MedDoseAttrs) => {
  const dosage = formatMedDosage(a)
  const freq = a.frequency_label?.trim()
  return freq ? `${dosage} · ${freq}` : dosage
}

/** Badge must not stretch — wrap so Grommet Button stays content-sized */
export const statusBadge = (status: MedStatus | DispenseStatus | string) => (
  <span className="inline-flex shrink-0 items-center">
    <Badge label={status.replace(/_/g, ' ')} size="small" status={status} />
  </span>
)

/** Small tag for an order the patient buys outside (shown in lists; such orders skip the queues). */
export const BuyOutsideTag = () => (
  <span className="inline-flex h-5 shrink-0 items-center rounded-full border border-[#D0D5DD] bg-[#F2F4F7] px-2 text-[11px] leading-none text-[#475467]">
    {BUY_OUTSIDE_LABEL}
  </span>
)

export const OrderStatusPill = ({ status }: { status: MedStatus | DispenseStatus | string }) => {
  const normalized = status.replace(/_/g, ' ')
  const isGood = status === 'verified' || status === 'dispensed'
  return (
    <span
      className={`inline-flex h-5 shrink-0 items-center rounded-full border px-2 text-[11px] leading-none ${
        isGood ? 'border-[#8EE6AA] bg-[#D9FBE3] text-[#008C35]' : 'border-[#FFD48A] bg-[#FFF2D7] text-[#E18A00]'
      }`}
    >
      {normalized.charAt(0).toUpperCase() + normalized.slice(1)}
    </span>
  )
}

export const PanelCard = ({
  title,
  children,
  hint,
  className = ''
}: {
  title?: string
  children: ReactNode
  hint?: string
  className?: string
}) => (
  <div
    className={`rounded-lg border border-[#E6E6E6] bg-white p-4 ${className}`}
    style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
  >
    {(title || hint) && (
      <div className="flex flex-col gap-1">
        {title ? <p className="m-0 text-base font-medium text-black">{title}</p> : null}
        {hint ? <p className="m-0 text-sm text-[#5C6C71]">{hint}</p> : null}
      </div>
    )}
    {children}
  </div>
)

export const MedOrderCard = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <div className={`rounded-[10px] border border-[#D9D9D9] bg-white p-5 ${className}`}>{children}</div>
)

export const MedOrderDetailBlock = ({ children }: { children: ReactNode }) => (
  <div className="rounded-[10px] bg-[#F4F4F4] px-4 py-4 text-sm text-[#3D3D3D]">{children}</div>
)

export const EmptyState = ({ message }: { message: string }) => (
  <div className="flex items-center justify-center py-10">
    <p className="m-0 text-sm text-[#5C6C71]">{message}</p>
  </div>
)

export const Field = ({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) => (
  <label className="flex w-full flex-col gap-1.5">
    <span className="text-sm font-medium text-[#1A1A1A]">{label}</span>
    {hint ? <span className="-mt-0.5 text-xs font-normal text-[#5C6C71]">{hint}</span> : null}
    {children}
  </label>
)

const controlClass =
  'box-border w-full rounded-[22px] border border-[#C8C8C8] bg-white text-sm text-[#1A1A1A] outline-none transition-colors focus:border-[#B4A33D] disabled:cursor-not-allowed disabled:bg-[#F5F5F5]'

const CHEVRON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%238B7355' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"

type SelectOption = { label: string; value: string }

type StyledSelectProps = {
  options: SelectOption[]
  value?: string
  onChange: (event: { value: string }) => void
  /** Kept for call-site compatibility with the old Grommet Select API */
  labelKey?: string
  valueKey?: { key: string; reduce?: boolean }
  a11yTitle?: string
  placeholder?: string
  disabled?: boolean
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value' | 'children'>

/** Native select — one border, caret inside the field */
export const StyledSelect = ({
  options,
  value,
  onChange,
  a11yTitle,
  placeholder,
  disabled,
  labelKey: _labelKey,
  valueKey: _valueKey,
  className = '',
  style,
  ...rest
}: StyledSelectProps) => (
  <select
    {...rest}
    aria-label={a11yTitle}
    className={`${controlClass} h-12 min-h-12 appearance-none pl-4 pr-10 ${className}`}
    disabled={disabled}
    onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange({ value: e.target.value })}
    style={{
      backgroundImage: `url("${CHEVRON}")`,
      backgroundPosition: 'right 14px center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '12px 8px',
      ...style
    }}
    value={value ?? ''}
  >
    {placeholder ? (
      <option disabled value="">
        {placeholder}
      </option>
    ) : null}
    {options.map(opt => (
      <option key={opt.value === '' ? '__empty__' : opt.value} value={opt.value}>
        {opt.label}
      </option>
    ))}
  </select>
)

export const StyledTextInput = ({ className = '', style, ...props }: InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} className={`${controlClass} h-12 min-h-12 px-4 ${className}`} style={style} />
)

export const StyledTextArea = ({ className = '', style, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    {...props}
    className={`${controlClass} min-h-[154px] resize-y rounded-[6px] p-3 ${className}`}
    style={style}
  />
)

export const ListRow = ({
  active,
  onClick,
  left,
  right
}: {
  active?: boolean
  onClick?: () => void
  left: ReactNode
  right?: ReactNode
}) => (
  <div
    className={`flex w-full items-center justify-between gap-3 rounded-[9px] border px-4 py-4 ${
      active ? 'border-[#A7CBFF] bg-[#EEF6FF]' : 'border-[#D9D9D9] bg-white'
    } ${onClick ? 'cursor-pointer hover:bg-[#FAFAFA]' : ''}`}
    onClick={onClick}
    onKeyDown={
      onClick
        ? e => {
            if (e.key === 'Enter' || e.key === ' ') onClick()
          }
        : undefined
    }
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
  >
    <div className="min-w-0 flex-1">{left}</div>
    {right ? <div className="flex shrink-0 items-center gap-2">{right}</div> : null}
  </div>
)

export const ModeToggle = ({
  mode,
  onChange
}: {
  mode: 'hospital' | 'community'
  onChange: (m: 'hospital' | 'community') => void
}) => (
  <div className="inline-flex rounded-full border border-[#E6E6E6] bg-white p-1">
    {(['hospital', 'community'] as const).map(m => (
      <button
        className={`rounded-full px-4 py-1.5 text-sm capitalize transition ${
          mode === m ? 'bg-[#E9EEF6] text-[#1A1A1A]' : 'bg-transparent text-[#5C6C71] hover:text-black'
        }`}
        key={m}
        onClick={() => onChange(m)}
        type="button"
      >
        {m}
      </button>
    ))}
  </div>
)

/** High-contrast workspace tabs — easier to spot than underline Tab3 in dense layouts */
export const WorkspaceTabs = ({
  tabs,
  activeTab,
  onChange,
  backTab
}: {
  tabs: string[]
  activeTab: string
  onChange: (tab: string) => void
  backTab?: string
}) => (
  <div className="flex flex-wrap gap-2">
    {tabs.map(tab => {
      const isActive = activeTab === tab
      return (
        <button
          className={`inline-flex h-7 items-center gap-2 rounded-[8px] border px-3 text-sm transition ${
            isActive
              ? 'border-[#B4A000] bg-[#B4A000] text-white'
              : 'border-[#D9D9D9] bg-white text-[#2A2A2A] hover:bg-[#F5F5F5]'
          }`}
          key={tab}
          onClick={() => onChange(tab)}
          type="button"
        >
          {tab === backTab ? <LinkPrevious color="currentColor" size="14px" /> : null}
          {tab === backTab ? 'To Order' : tab}
        </button>
      )
    })}
  </div>
)

export const ActionRow = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-wrap items-center gap-2 pt-1">{children}</div>
)

export const SmButton = (props: ComponentProps<typeof Button>) => {
  const kind = (props as { kinds?: string }).kinds
  return (
    <Button
      {...props}
      className={`!h-10 !rounded-full !px-4 !py-0 !text-sm ${
        kind === 'primary' ? '!bg-[#B4A33D]' : ''
      } ${props.className || ''}`}
      size="sm"
    />
  )
}

/** Group medications by patient id — used to keep Verify/Fill one patient at a time. */
export const groupMedsByPatient = <T extends { relationships: { patient: { data: { id: string } | null } } }>(
  meds: T[]
): { patientId: string; meds: T[] }[] => {
  const map = new Map<string, T[]>()
  for (const m of meds) {
    const pid = m.relationships.patient.data?.id
    if (!pid) continue
    const list = map.get(pid)
    if (list) list.push(m)
    else map.set(pid, [m])
  }
  return Array.from(map.entries()).map(([patientId, patientMeds]) => ({
    patientId,
    meds: patientMeds
  }))
}
